import { z } from "zod";
import type { FronteggIdentityRole } from "./users.utils";

/** Loose row from GET …/identity/resources/roles/v1 before id/name normalization. */
const fronteggRoleRowSchema = z
  .object({
    id: z.string().nullish(),
    key: z.string().nullish(),
    name: z.string().nullish(),
    level: z.union([z.number(), z.string()]).nullish(),
    isDefault: z.union([z.boolean(), z.string()]).nullish(),
    description: z.string().nullish(),
  })
  .passthrough();

function extractFronteggRolesRows(json: unknown): unknown[] {
  if (Array.isArray(json)) {
    return json;
  }
  if (!json || typeof json !== "object") {
    return [];
  }
  const root = json as Record<string, unknown>;
  const nested = root.items ?? root.roles ?? root.data;
  return Array.isArray(nested) ? nested : [];
}

/**
 * Normalizes a roles/v1 row onto {@link FronteggIdentityRole}, using `key` when `id` is absent.
 */
function normalizeFronteggRoleRow(
  row: z.infer<typeof fronteggRoleRowSchema>,
): FronteggIdentityRole | null {
  const id = row.id ?? row.key;
  if (!id) {
    return null;
  }
  const level =
    typeof row.level === "string" ? Number(row.level) : row.level ?? undefined;
  const isDefault =
    typeof row.isDefault === "string"
      ? row.isDefault === "true"
      : row.isDefault ?? undefined;

  return {
    ...row,
    description: row.description ?? undefined,
    id,
    isDefault,
    key: row.key ?? undefined,
    level: Number.isFinite(level) ? level : undefined,
    name: row.name ?? row.key ?? id,
  };
}

/**
 * Normalizes GET …/resources/roles/v1 payloads for role pickers (invite user, edit roles, groups).
 * Accepts a bare role array or `{ items | roles | data }` wrappers; drops rows without id/key.
 */
export function parseFronteggRolesPayload(
  json: unknown,
): FronteggIdentityRole[] {
  return extractFronteggRolesRows(json).flatMap((row) => {
    const parsed = fronteggRoleRowSchema.safeParse(row);
    if (!parsed.success) {
      return [];
    }
    const normalized = normalizeFronteggRoleRow(parsed.data);
    return normalized ? [normalized] : [];
  });
}

/** Minimal role identity from the signed-in Frontegg JWT / user object. */
export type AuthActorRoleRef = {
  id?: string;
  key?: string;
  name?: string;
};

export type AuthRolePickerOption = {
  role: FronteggIdentityRole;
  assignable: boolean;
};

function matchCatalogRole(
  catalog: FronteggIdentityRole[],
  actorRole: AuthActorRoleRef,
): FronteggIdentityRole | undefined {
  if (actorRole.id) {
    const byId = catalog.find((role) => role.id === actorRole.id);
    if (byId) {
      return byId;
    }
  }
  if (actorRole.key) {
    const byKey = catalog.find(
      (role) => role.key === actorRole.key || role.id === actorRole.key,
    );
    if (byKey) {
      return byKey;
    }
  }
  if (actorRole.name) {
    return catalog.find(
      (role) => role.name === actorRole.name || role.key === actorRole.name,
    );
  }
  return undefined;
}

/**
 * Highest catalog `level` among the actor's JWT roles (higher number = stronger).
 * Returns `undefined` when no actor role matches a catalog row with a numeric level.
 */
export function getActorMaxRoleLevel(
  catalog: FronteggIdentityRole[],
  actorRoles: AuthActorRoleRef[],
): number | undefined {
  const levels = actorRoles.flatMap((actorRole) => {
    const matched = matchCatalogRole(catalog, actorRole);
    if (
      matched &&
      typeof matched.level === "number" &&
      Number.isFinite(matched.level)
    ) {
      return [matched.level];
    }
    return [];
  });
  if (levels.length === 0) {
    return undefined;
  }
  return Math.max(...levels);
}

/**
 * Whether the actor may assign `role` under hierarchy rule level <= actorMaxLevel.
 * Roles without a numeric level are never assignable.
 */
export function isRoleAssignable(
  role: FronteggIdentityRole,
  actorMaxLevel: number | undefined,
): boolean {
  if (actorMaxLevel === undefined) {
    return false;
  }
  if (typeof role.level !== "number" || !Number.isFinite(role.level)) {
    return false;
  }
  return role.level <= actorMaxLevel;
}

/**
 * Builds picker rows: assignable roles always; non-assignable only when listed in
 * `preserveRoleIds` (checked + disabled in edit flows).
 */
export function toAuthRolePickerOptions(
  catalog: FronteggIdentityRole[],
  actorRoles: AuthActorRoleRef[],
  options?: { preserveRoleIds?: string[] },
): AuthRolePickerOption[] {
  const actorMaxLevel = getActorMaxRoleLevel(catalog, actorRoles);
  const preserveRoleIds = new Set(options?.preserveRoleIds ?? []);
  return catalog.flatMap((role) => {
    const assignable = isRoleAssignable(role, actorMaxLevel);
    if (!assignable && !preserveRoleIds.has(role.id)) {
      return [];
    }
    return [{ role, assignable }];
  });
}

/**
 * Returns only role IDs the actor may assign. Throws when any requested id is
 * above the actor's level or missing from the catalog.
 */
export function assertAssignableRoleIds(
  catalog: FronteggIdentityRole[],
  actorRoles: AuthActorRoleRef[],
  roleIds: string[],
): string[] {
  const actorMaxLevel = getActorMaxRoleLevel(catalog, actorRoles);
  const roleById = new Map(catalog.map((role) => [role.id, role]));
  const illegalRoleIds = roleIds.filter((roleId) => {
    const role = roleById.get(roleId);
    return !role || !isRoleAssignable(role, actorMaxLevel);
  });
  if (illegalRoleIds.length > 0) {
    throw new Error(
      `Cannot assign roles above your level: ${illegalRoleIds.join(", ")}`,
    );
  }
  return roleIds;
}

/**
 * Sanitizes a role update: blocks adding above-actor roles, and keeps
 * non-assignable current roles even if omitted from `nextRoleIds`.
 */
export function sanitizeRoleAssignmentDiff(options: {
  catalog: FronteggIdentityRole[];
  actorRoles: AuthActorRoleRef[];
  nextRoleIds: string[];
  currentRoleIds: string[];
}): { addRoleIds: string[]; removeRoleIds: string[] } {
  const actorMaxLevel = getActorMaxRoleLevel(
    options.catalog,
    options.actorRoles,
  );
  const roleById = new Map(options.catalog.map((role) => [role.id, role]));

  function canAssign(roleId: string): boolean {
    const role = roleById.get(roleId);
    if (!role) {
      return false;
    }
    return isRoleAssignable(role, actorMaxLevel);
  }

  const currentSet = new Set(options.currentRoleIds);
  const illegalAdds = options.nextRoleIds.filter(
    (roleId) => !currentSet.has(roleId) && !canAssign(roleId),
  );
  if (illegalAdds.length > 0) {
    throw new Error(
      `Cannot assign roles above your level: ${illegalAdds.join(", ")}`,
    );
  }

  const preservedCurrent = options.currentRoleIds.filter(
    (roleId) => !canAssign(roleId),
  );
  const assignableNext = options.nextRoleIds.filter((roleId) =>
    canAssign(roleId),
  );
  const sanitizedNext = [...new Set([...assignableNext, ...preservedCurrent])];

  return diffRoleIds(options.currentRoleIds, sanitizedNext);
}

/**
 * Diffs current vs next role IDs so assign/unassign can run as separate calls.
 */
export function diffRoleIds(currentIds: string[], nextIds: string[]) {
  const current = new Set(currentIds);
  const next = new Set(nextIds);
  return {
    addRoleIds: nextIds.filter((id) => !current.has(id)),
    removeRoleIds: currentIds.filter((id) => !next.has(id)),
  };
}
